import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { ICriticalCareState } from '../../procedures/criticalCare/store/criticalCare.types'
import { IOptions } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { handleNemsisCodeError, getUpdatedProcedureStateWithNemsisCode } from '../../../utilities/nemsisCode.util'

interface CreateCriticalCareProcedureProps {
  procedureEvent: IEKGEvent
  criticalCareOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface CriticalCareProcedureObject {
  timelineItem: IItem
  criticalCareState: ICriticalCareState
}

const createCriticalCareProcedure = (props: CreateCriticalCareProcedureProps): CriticalCareProcedureObject => {
  const { procedureEvent, criticalCareOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const criticalCareOption = criticalCareOptions.find(
    (option) => (option.id ?? '').toString() === procedureEvent.procedureAction
  )
  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Action: ${
    criticalCareOption?.name || ''
  }, Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.CRITICAL_CARE_PROCEDURE,
    criticalCareOption?.name || '',
    { filter1: '', filter2: '', filter3: '' },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Critical Care',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_CRITICAL_CARE_PROCEDURE_ITEM,
    defaultHref: TabConstants.CRITICAL_CARE_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let criticalCareState: ICriticalCareState = {
    key: clientEventId,
    action: criticalCareOption ?? null,
    site: null,
    task: null,
    transducerZeroedPre: null,
    transducerZeroedPost: null,
    triggerUtilizedPre: null,
    triggerUtilizedPost: null,
    IABPFrequencyPre: null,
    IABPFrequencyPost: null,
    IABPAugmentationPre: null,
    IABPAugmentationPost: null,
    slowGasLeakAlarmPre: null,
    slowGasLeakAlarmPost: null,
    timingPre: null,
    timingPost: null,
    IABPFillPre: null,
    IABPFillPost: null,
    augmentationAlarmSetPre: null,
    augmentationAlarmSetPost: null,
    leftBrachialPulsePre: null,
    leftBrachialPulsePost: null,
    distalPulsePre: null,
    distalPulsePost: null,
    extremityRestrainedPre: null,
    extremityRestrainedPost: null,
    insertionSitePulsePost: null,
    insertionSitePulsePre: null,
    inflationPointPre: '',
    inflationPointPost: '',
    deflationPointPre: '',
    deflationPointPost: '',
    diastolicAugmentationPre: '',
    diastolicAugmentationPost: '',
    unaugmentedDiastolePre: '',
    unaugmentedDiastolePost: '',
    diastolicDipPre: '',
    diastolicDipPost: '',
    augmentedSystolePre: '',
    augmentedSystolePost: '',
    unaugmentedSystolePre: '',
    unaugmentedSystolePost: '',
    size: '',
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'CriticalCare',
    rolePerforming: null,
    rolePerformingPNNV: null,
    verificationMethod: [],
    isEkgImport: 1,
    criticalCareDefaults: null,
    reasonsForFailure: [],
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    criticalCareState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: criticalCareState,
      actionField: null,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    criticalCareState = handleNemsisCodeError(error, criticalCareState)
  }

  const criticalCareProcedureObject: CriticalCareProcedureObject = {
    criticalCareState,
    timelineItem,
  }

  return criticalCareProcedureObject
}

export default createCriticalCareProcedure
